import { logoutUser } from "../actions/authActions";
import store from "../store";
import axios from "axios";
import { apiUrl } from "../config/config";
import { clearVerifierCookies, writeHeartbeat, readHeartbeat, readBreakState, writeBreakState } from "./crossSubdomainCookies";

// Returns the trimmed string only if it is a real, non-empty value.
// Filters out null, undefined, and the literal strings "null"/"undefined"
// that older code paths sometimes wrote into localStorage.
const isValidStored = (v) => {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  if (!t || t === "null" || t === "undefined") return null;
  return t;
};

class IdleTimer {
  constructor() {
    // Each verifier uses a single platform (web1/web2/web4) at a time.
    // If they switch platforms without explicit logout, the previous tab
    // sits idle — and historically kept accruing Worked time, inflating
    // each verifier's daily duration. Auto-logout at 2 minutes of zero
    // activity closes the stale session so total Worked stays accurate.
    this.idleThreshold = 3 * 60 * 1000; // 3 minutes → auto logout
    this.breakThreshold = 60 * 1000;    // 1 minute → start counting as break
    // ── Also fires logout on page-hide ─────────────────────────────
    // The 2-min timer can only fire while the tab is open. Closing the
    // tab / navigating away never trips it, so we add a navigator.send-
    // Beacon hook in start() to write LoggedoutTime on pagehide too.
    // See start() below for the implementation.
    this.events = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    this.eventHandler = this.onActivity.bind(this);

    this.idleTimer = null;
    this.countdownInterval = null;
    this.remainingTime = this.idleThreshold;
    this.breakCheckInterval = null;
    
    // Initialize break tracking
    this.breakTime = parseFloat(localStorage.getItem('breakTime')) || 0;
    this.isOnBreak = localStorage.getItem('isOnBreak') === 'true';
    const storedBreakStartTime = localStorage.getItem('breakStartTime');
    this.breakStartTime = storedBreakStartTime ? parseInt(storedBreakStartTime) : null;
    
    // console.log('Initial break time:', this.breakTime);
    // console.log('Initial break state:', this.isOnBreak);
    // console.log('Initial break start time:', this.breakStartTime);

    this.breakTime = this.getStoredBreakTime();
    this.breakStartTime = null;
    this.isOnBreak = false;
    this.startTime = this.getStoredLoginTime() || Date.now();
    this.lastActivityTime = this.startTime; // initialize last activity time
    this.activityPercentage = 0; // 🟢 On initial login: activityPercentage = 0
    localStorage.setItem("activityPercentage", this.activityPercentage);

    // 🟢 Listen for activityPercentage changes from other tabs (real-time sync)
    window.addEventListener("storage", (event) => {
      if (event.key === "activityPercentage") {
        const newValue = parseInt(event.newValue, 10) || 0;
        if (this.activityPercentage !== newValue) {
          this.activityPercentage = newValue;
          // Optionally, update UI or trigger any listeners here for real-time effect
        }
      }
    });

    // 🟢 Listen for focus to immediately sync activityPercentage from storage
    window.addEventListener("focus", () => {
      const latest =
        parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      if (this.activityPercentage !== latest) {
        this.activityPercentage = latest;
        // Optionally, update UI or trigger any listeners here for real-time effect
      }
    });

    this.start();
    this.activityMonitor();
  }

  start() {
    this.storeLoginTime();
    this.events.forEach((event) =>
      window.addEventListener(event, this.eventHandler)
    );
    document.addEventListener(
      "visibilitychange",
      this.handleVisibility.bind(this)
    );

    // Fire logout via navigator.sendBeacon when the tab is closed or
    // the user switches to another platform's tab and the OS unloads
    // this one. Beacon survives the page teardown that would kill a
    // normal axios.post(). This is what catches "switched to web2 so
    // web1's session should end" without waiting the full 2 minutes.
    this._beaconHandler = () => this.fireLogoutBeacon();
    window.addEventListener("pagehide", this._beaconHandler);
    window.addEventListener("beforeunload", this._beaconHandler);

    this.startCountdown();
  }

  // Fire-and-forget logout used by pagehide/beforeunload. Uses
  // navigator.sendBeacon (which the browser guarantees to deliver
  // even mid-unload) to write LoggedoutTime + a final activity flush.
  // Skips the exempt verifier IDs (same list the regular logout uses).
  fireLogoutBeacon() {
    try {
      const verifierId = (localStorage.getItem("verifierid") || "").trim();
      const loginedTime = (localStorage.getItem("loginedDateTime") || "").trim();
      const verifierType = localStorage.getItem("verifiertype");
      if (
        !verifierId ||
        verifierId === "null" ||
        verifierId === "undefined" ||
        !loginedTime ||
        loginedTime === "null" ||
        loginedTime === "undefined"
      ) return;
      // Exempt kiosk / admin-overlay accounts (same list as logout()).
      if (
        verifierId === "5d6df589c3d92110fccfde7c" ||
        verifierType === "7" ||
        verifierId === "688d9d4acebbc60f077de5e2" ||
        verifierId === "688d9c64cebbc60f077de5de"
      ) return;

      const apiBase = window.location.origin + "/api/users/";
      // sendBeacon needs a Blob with the right MIME type so the
      // Node body-parser still sees JSON on the receiving end.
      const payload = (obj) =>
        new Blob([JSON.stringify(obj)], { type: "application/json" });

      navigator.sendBeacon(
        apiBase + "updateLogoutTime",
        payload({ Verifier_id: verifierId, LoginedTime: loginedTime })
      );

      const activityPct = Number(localStorage.getItem("activityPercentage"));
      const breakTime = Number(localStorage.getItem("breakTime"));
      navigator.sendBeacon(
        apiBase + "UpdateVerifierActivity",
        payload({
          Verifier_id: verifierId,
          LoginedTime: loginedTime,
          ActivityLevel: Number.isFinite(activityPct) ? Math.round(activityPct) : 0,
          BreaksConsumed: Number.isFinite(breakTime) ? Math.round(breakTime) : 0,
        })
      );
    } catch (_) {
      // Swallow — beacon failures are diagnostic only and unloading.
    }
  }

  onActivity() {
    this.remainingTime = this.idleThreshold; // reset to 2 minutes
    writeHeartbeat();
    const now = Date.now();

    // Debug logging
    // console.log('Activity detected at:', new Date(now).toLocaleTimeString());
    // console.log('Current break state:', this.isOnBreak ? 'On Break' : 'Active');
    
    // Check if returning from a break
    if (this.isOnBreak) {
      // console.log('Returning from break...');
      this.endBreak();
    }

    // Update last activity time only if at least 500ms has passed (prevent rapid counting)
    if (!this.lastActivityTime || now - this.lastActivityTime > 500) {
      const previousActivityTime = this.lastActivityTime;
      this.lastActivityTime = now;

      // Calculate active duration since login
      const loginTime = this.getStoredLoginTime() || now;
      const totalTimeInMinutes = (now - loginTime) / (60 * 1000);
      // Cap break at elapsed time — break can never exceed the time since
      // login, so any larger value is stale localStorage state from a prior
      // session that didn't reset cleanly.
      const safeBreak = Math.min(
        Math.max(0, Number(this.breakTime) || 0),
        Math.max(0, totalTimeInMinutes)
      );
      this.breakTime = safeBreak;
      const activeTimeInMinutes = totalTimeInMinutes - safeBreak;

      // Guard against divide-by-zero when login just happened.
      let activityPercentage =
        totalTimeInMinutes > 0
          ? (activeTimeInMinutes / totalTimeInMinutes) * 100
          : 100;

      // Ensure activity percentage doesn't drop too quickly
      const currentPercentage = parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      const maxDrop = 5; // Maximum percentage drop allowed
      activityPercentage = Math.max(activityPercentage, currentPercentage - maxDrop);

      // If user is actively working, boost slightly
      if (previousActivityTime && (now - previousActivityTime) < 30000) { // Active in last 30 seconds
        activityPercentage = activityPercentage + 1;
      }

      // Final clamp + round. Without [0,100] bounds a stale break value
      // can produce huge negative percentages that get sent to the server.
      activityPercentage = Math.max(0, Math.min(100, Math.round(activityPercentage)));

      this.activityPercentage = activityPercentage;
      localStorage.setItem("activityPercentage", activityPercentage.toString());
      
      // console.log('Updated activity:', {
      //   lastActivityTime: new Date(this.lastActivityTime).toLocaleTimeString(),
      //   totalTimeInMinutes: Math.round(totalTimeInMinutes),
      //   activeTimeInMinutes: Math.round(activeTimeInMinutes),
      //   activityPercentage: Math.round(activityPercentage),
      //   breakTime: Math.round(this.breakTime)
      // });
    }
  }

  startCountdown() {
    this.clearTimers();

    // Main countdown for logout
    this.countdownInterval = setInterval(() => {
      this.remainingTime -= 1000;
      this.storeRemainingTime();

      if (this.remainingTime <= 0) {
        this.logout();
      }
    }, 1000);

    // Separate interval for break checking (check every second).
    // Uses the shared heartbeat cookie so activity in a sibling subdomain
    // (e.g. report app) counts as "active here" too — no false breaks when
    // the verifier is working on another platform.
    this.breakCheckInterval = setInterval(() => {
      const now = Date.now();
      const lastShared = Math.max(this.lastActivityTime, readHeartbeat());
      const timeSinceLastActivity = now - lastShared;
      const shared = readBreakState();

      // Mirror cookie → local in BOTH directions. Without the false-side
      // mirror, when the report tab ends a break we'd still call endBreak()
      // ourselves and re-add the full duration, double-counting it.
      if (shared.isOnBreak && !this.isOnBreak) {
        this.isOnBreak = true;
        this.breakStartTime = shared.breakStartTime;
      } else if (!shared.isOnBreak && this.isOnBreak) {
        this.isOnBreak = false;
        this.breakStartTime = null;
        this.breakTime = Math.max(parseFloat(this.breakTime) || 0, shared.breakTime || 0);
        localStorage.setItem('breakTime', this.breakTime.toString());
        localStorage.removeItem('breakStartTime');
        localStorage.setItem('isOnBreak', 'false');
        return;
      }

      if (timeSinceLastActivity >= this.breakThreshold) {
        if (!this.isOnBreak) {
          this.startBreak();
        }
      } else if (this.isOnBreak) {
        this.endBreak();
      }
    }, 1000);
  }

  clearTimers() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.breakCheckInterval) {
      clearInterval(this.breakCheckInterval);
    }
    // Also cancel the background-tab hidden-logout timer so a stale
    // one doesn't fire after we've already torn down (e.g. on real
    // logout or page navigation).
    if (this.hiddenLogoutTimer) {
      clearTimeout(this.hiddenLogoutTimer);
      this.hiddenLogoutTimer = null;
    }
  }

  storeRemainingTime() {
    const minutesLeft = (this.remainingTime / 1000 / 60).toFixed(2);
    localStorage.setItem("remainingLogoutTime", minutesLeft);
  }

  storeLoginTime() {
    if (!localStorage.getItem("loggedInTime")) {
      localStorage.setItem("loggedInTime", new Date().toISOString());
    }
  }

  getStoredLoginTime() {
    const time = localStorage.getItem("loggedInTime");
    return time ? new Date(time).getTime() : null;
  }

  storeBreakTime() {
    // Store break time with 2 decimal places
    const breakTimeToStore = Math.round(this.breakTime * 100) / 100;
    localStorage.setItem("breakTime", breakTimeToStore.toString());
    // console.log('Stored break time:', breakTimeToStore, 'minutes');
  }

  getStoredBreakTime() {
    const storedTime = Math.round(parseFloat(localStorage.getItem("breakTime")) * 100) / 100 || 0;
    // console.log('Retrieved stored break time:', storedTime, 'minutes');
    return storedTime;
  }

  handleVisibility() {
    if (document.hidden) {
      // Tab just went to the background. The mouse / keyboard event
      // listeners on `window` will receive NOTHING while the user is on
      // another tab (the OS routes input to the foreground tab only),
      // and Chrome throttles setInterval in background tabs so the
      // 1-sec idle countdown can't be trusted. Start a one-shot
      // setTimeout — it's also throttled, but only needs to fire once
      // and will land within a few seconds of the threshold.
      //
      // Use case: verifier opens web2 in a new tab and works there.
      // web1's tab goes hidden → this timer starts → after 2 min
      // without coming back, web1 logs the verifier out and the
      // Worked-time accumulator on web1 stops.
      if (!this.hiddenLogoutTimer) {
        this.hiddenLogoutTimer = setTimeout(() => {
          this.hiddenLogoutTimer = null;
          this.logout();
        }, this.idleThreshold);
      }
    } else {
      // Tab is visible again — verifier came back. Cancel the
      // pending hidden-logout, then treat the return as activity so
      // the in-tab idle countdown also resets.
      if (this.hiddenLogoutTimer) {
        clearTimeout(this.hiddenLogoutTimer);
        this.hiddenLogoutTimer = null;
      }
      this.onActivity();
    }
  }

  async logout() {
    // Sibling subdomain (report app) heartbeat: if a verifier is actively
    // using reportweb*.mybiocalculus.com, treat that as a live session and
    // defer the dashboard's own idle-logout instead of killing cookies.
    // Background tabs have their countdownInterval throttled to ~1Hz max,
    // so we also re-arm the hidden-tab timer to guarantee another check
    // even if the wall-clock countdown runs slow.
    const heartbeat = readHeartbeat();
    if (heartbeat && Date.now() - heartbeat < this.idleThreshold) {
      this.remainingTime = this.idleThreshold;
      if (document.hidden && !this.hiddenLogoutTimer) {
        this.hiddenLogoutTimer = setTimeout(() => {
          this.hiddenLogoutTimer = null;
          this.logout();
        }, this.idleThreshold);
      }
      return;
    }

    this.clearTimers();

    const verifierId = isValidStored(localStorage.getItem("verifierid"));
    const verifierType = localStorage.getItem("verifiertype");
    const loginedTime = isValidStored(localStorage.getItem("loginedDateTime"));

    if (
      verifierId === "5d6df589c3d92110fccfde7c" ||
      verifierType === "7" ||
      verifierId === "688d9d4acebbc60f077de5e2" ||
      verifierId === "688d9c64cebbc60f077de5de"
    ) {
      // console.log("Verifier type 7 - logout skipped");
      return;
    }

    if (verifierId && loginedTime) {
      try {
        await axios.post(`${apiUrl}api/users/updateLogoutTime`, {
          Verifier_id: verifierId,
          LoginedTime: loginedTime,
        });

        const activityPct = Number(localStorage.getItem("activityPercentage"));
        await axios.post(`${apiUrl}api/users/UpdateVerifierActivity`, {
          Verifier_id: verifierId,
          LoginedTime: loginedTime,
          ActivityLevel: Number.isFinite(activityPct) ? Math.round(activityPct) : 0,
          BreaksConsumed: Number.isFinite(this.breakTime) ? Math.round(this.breakTime) : 0,
        });
      } catch (err) {
        // console.log("Logout API error:", err);
      }
    }
    this.events.forEach((event) =>
      window.removeEventListener(event, this.eventHandler)
    );
    document.removeEventListener("visibilitychange", this.handleVisibility);

    clearVerifierCookies();
    localStorage.clear();
    store.dispatch(logoutUser());
  }

  activityMonitor() {
    setInterval(() => {
      this.updateActivityLevel();
    }, 60000); // Changed interval to 1 minute (60000 ms)
  }

  updateActivityLevel() {
    const now = Date.now();
    const storedLogin = this.getStoredLoginTime();
    const loginTime = storedLogin || now;

    // Pull the latest shared break total so this POST reflects break time
    // another platform may have accrued since our last sync.
    const sharedBreak = readBreakState();
    this.breakTime = Math.max(parseFloat(this.breakTime) || 0, sharedBreak.breakTime || 0);

    // Calculate total time and active time
    const totalTimeInMinutes = (now - loginTime) / (60 * 1000);
    // Cap break at elapsed time — break can never exceed the time since
    // login. Anything larger is stale state from a prior session.
    const safeBreak = Math.min(
      Math.max(0, Number.isFinite(this.breakTime) ? this.breakTime : 0),
      Math.max(0, totalTimeInMinutes)
    );
    this.breakTime = safeBreak;
    const activeTimeInMinutes = totalTimeInMinutes - safeBreak;

    // Guard against divide-by-zero when login time is missing or just set.
    let activityPercentage =
      totalTimeInMinutes > 0
        ? (activeTimeInMinutes / totalTimeInMinutes) * 100
        : 100;

    // Factor in recent activity
    const timeSinceLastActivity = now - this.lastActivityTime;
    if (timeSinceLastActivity <= 30000) { // Active in last 30 seconds
      activityPercentage = activityPercentage + 2;
    } else if (timeSinceLastActivity > this.breakThreshold) { // Inactive for more than break threshold
      activityPercentage = activityPercentage - 5;
    }

    // Final clamp + round. Activity is a percentage — it must never leave
    // [0, 100], regardless of what the math upstream produced.
    activityPercentage = Math.max(
      0,
      Math.min(100, Math.round(Number.isFinite(activityPercentage) ? activityPercentage : 0))
    );

    // Update stored values
    this.activityPercentage = activityPercentage;
    localStorage.setItem("activityPercentage", activityPercentage.toString());

    // Skip the server update entirely if we don't have a real login session —
    // otherwise the server returns 400 every minute on the public dashboard route.
    const _verifierId = isValidStored(localStorage.getItem("verifierid"));
    const _loginedTime = isValidStored(localStorage.getItem("loginedDateTime"));
    if (!_verifierId || !_loginedTime || !storedLogin) {
      return;
    }

    axios
      .post(`${apiUrl}api/users/UpdateVerifierActivity`, {
        Verifier_id: _verifierId,
        LoginedTime: _loginedTime,
        ActivityLevel: activityPercentage,
        BreaksConsumed: Math.round(safeBreak),
      })
      .catch(() => {
        // swallow — server-side failures are surfaced via network tab
      });
  }

  getActivityPercentage() {
    // Calculate percentage based on time since last activity (Web Work method)
    const now = Date.now();
    if (!this.lastActivityTime) {
      const stored = localStorage.getItem("activityPercentage");
      return stored !== null ? parseInt(stored, 10) : 0;
    }
    const lastActivity = this.lastActivityTime || this.startTime || now;
    const elapsed = now - lastActivity;
    // Web Work method: 100 - Math.floor((elapsed / idleThreshold) * 100)
    const percent = Math.max(
      0,
      100 - Math.floor((elapsed / this.idleThreshold) * 100)
    );
    return percent;
  }

  startBreak() {
    if (this.isOnBreak) return;
    // If a sibling platform already started a break, adopt its breakStartTime
    // instead of creating a second one — both tabs converge to the same window.
    const sharedBreak = readBreakState();
    if (sharedBreak.isOnBreak && sharedBreak.breakStartTime) {
      this.isOnBreak = true;
      this.breakStartTime = sharedBreak.breakStartTime;
      return;
    }
    const now = Date.now();
    this.isOnBreak = true;
    this.breakStartTime = now;
    localStorage.setItem('breakStartTime', this.breakStartTime.toString());
    localStorage.setItem('isOnBreak', 'true');
    writeBreakState({ isOnBreak: true, breakStartTime: this.breakStartTime });
  }

  endBreak() {
    if (this.isOnBreak && this.breakStartTime) {
      const shared = readBreakState();
      // Idempotency guard: if the cookie already shows the break ended,
      // another tab beat us to it and already added the duration. Just
      // sync the merged total without re-adding our local duration.
      if (!shared.isOnBreak) {
        this.isOnBreak = false;
        this.breakStartTime = null;
        this.breakTime = Math.max(parseFloat(this.breakTime) || 0, shared.breakTime || 0);
        localStorage.setItem('breakTime', this.breakTime.toString());
        localStorage.removeItem('breakStartTime');
        localStorage.setItem('isOnBreak', 'false');
        return;
      }
      const breakEndTime = Date.now();
      const breakDuration = (breakEndTime - this.breakStartTime) / (60 * 1000);

      const base = Math.max(parseFloat(this.breakTime) || 0, shared.breakTime || 0);
      this.breakTime = Math.round((base + breakDuration) * 100) / 100;

      localStorage.setItem('breakTime', this.breakTime.toString());
      writeBreakState({
        breakTime: this.breakTime,
        isOnBreak: false,
        breakStartTime: null,
      });

      const verifierId = isValidStored(localStorage.getItem("verifierid"));
      const loginedTime = isValidStored(localStorage.getItem("loginedDateTime"));
      const activityPctRaw = Number(localStorage.getItem("activityPercentage"));
      const activityPct = Number.isFinite(activityPctRaw) ? activityPctRaw : 0;
      const safeBreak = Number.isFinite(this.breakTime) ? this.breakTime : 0;
      if (verifierId && loginedTime) {
        axios.post(`${apiUrl}api/users/UpdateVerifierActivity`, {
          Verifier_id: verifierId,
          LoginedTime: loginedTime,
          ActivityLevel: Math.round(activityPct),
          BreaksConsumed: Math.round(safeBreak),
        }).catch(() => {});
      }

      this.isOnBreak = false;
      this.breakStartTime = null;
      localStorage.removeItem('breakStartTime');
      localStorage.setItem('isOnBreak', 'false');
    }
  }

  // Helper method to get break time in minutes with 2 decimal places
  getFormattedBreakTime() {
    return this.breakTime.toFixed(2);
  }
}

export default IdleTimer;
